/**
 * 数据字典 Hook（mobile 版，对齐 admin useDict，但拆分为 Provider + useDict）。
 *
 * 单一事实来源：后端 sys_dict_data 表（/api/dict/data/batch）。
 * 移动端仅消费 doc_type 等展示用字典，不暴露管理动作。
 *
 * 工作机制：
 * - DictProvider 在 App 最外层包裹，监听登录态（isLoggedIn）；
 * - 登录后首次进入触发批量加载（dictCache），后续组件 useDict 命中本地缓存即可；
 * - 5 分钟 TTL 过期或 refresh() 主动触发重新拉取；
 * - 字典未命中时回退原始 code（绝不伪造中文名），与 nodeConfig 策略一致。
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { listAllDictData, type DictDataMap } from '@/api/dict';
import { useRole } from '@/store/RoleContext';

/** 模块级缓存（跨组件共享，避免重复请求；同一会话内命中即用） */
let dictCache: DictDataMap | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000;

interface DictContextValue {
  /** 全量字典数据 */
  dictData: DictDataMap;
  /** 加载中 */
  loading: boolean;
  /** 取指定类型下指定 code 的中文标签；查不到回退原始 code */
  getLabel: (dictType: string, code: string | null | undefined) => string;
  /** 主动重新拉取 */
  refresh: () => Promise<void>;
}

const DictContext = createContext<DictContextValue>({
  dictData: {},
  loading: false,
  getLabel: (_type, code) => code || '',
  refresh: async () => {},
});

export function DictProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useRole();
  const [dictData, setDictData] = useState<DictDataMap>(dictCache || {});
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async (force = false) => {
    if (!force && dictCache && Date.now() - cacheTimestamp < CACHE_TTL) {
      setDictData(dictCache);
      return;
    }
    setLoading(true);
    try {
      const data = (await listAllDictData()) || {};
      dictCache = data;
      cacheTimestamp = Date.now();
      setDictData(data);
    } catch (err) {
      console.error('[useDict] 加载字典数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 字典是平台级公开参考数据，挂载即加载（对齐 admin useDict 的无条件加载）。
  // 不再用 isLoggedIn 门控——这正是此前 mobile 偶发「全英文」的根因：
  // 登录态判断/加载时机差池会导致 dictData 为空、getLabel 回退成 code。
  // 登录后再强制刷新一次，覆盖「登录前已加载空缓存」场景；登出清缓存避免串数据。
  useEffect(() => {
    if (isLoggedIn) {
      loadAll(true);
    } else {
      dictCache = null;
      cacheTimestamp = 0;
      setDictData({});
      loadAll();
    }
  }, [isLoggedIn, loadAll]);

  const getLabel = useCallback(
    (dictType: string, code: string | null | undefined): string => {
      if (!code) return '';
      const label = dictData[dictType]?.[code];
      return label ?? code;
    },
    [dictData],
  );

  const refresh = useCallback(async () => {
    await loadAll(true);
  }, [loadAll]);

  return (
    <DictContext.Provider value={{ dictData, loading, getLabel, refresh }}>
      {children}
    </DictContext.Provider>
  );
}

/** 获取字典工具函数（必须在 DictProvider 内部使用） */
export function useDict(): DictContextValue {
  return useContext(DictContext);
}
